'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useInstallPrompt, useSWUpdate, useIsStandalone } from '@/lib/use-pwa';

/* ─────────────────────────────────────────────────────────
 * PWAPrompt
 * ─────────────────────────────────────────────────────────
 * Zwei Funktionen:
 *
 * 1. Install-Banner: Zeigt eine Benachrichtigung wenn die
 *    App noch nicht installiert ist (nach 30s Verzögerung).
 *    Der User kann installieren oder für 7 Tage ablehnen.
 *
 * 2. Update-Banner: Zeigt sofort eine Benachrichtigung
 *    wenn ein neuer Build verfügbar ist. Der User kann
 *    die App mit einem Klick aktualisieren.
 *
 * iOS-Hinweis: iOS unterstützt `beforeinstallprompt` nicht,
 * daher wird dort ein manueller Hinweis angezeigt.
 * ───────────────────────────────────────────────────────── */

const IOS_DISMISS_KEY = 'pwa-ios-install-dismissed';
const IOS_DISMISS_DURATION = 14 * 24 * 60 * 60 * 1000; // 14 Tage
const INSTALL_DELAY = 30_000; // 30 Sekunden warten bevor Install-Banner

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

export default function PWAPrompt() {
  const t = useTranslations('pwa');

  // ── Install-Prompt (Chrome/Edge/Android) ──
  const { canInstall, promptInstall, dismiss } = useInstallPrompt();
  const [showInstall, setShowInstall] = useState(false);

  // ── iOS-Hinweis ──
  const [showIOSHint, setShowIOSHint] = useState(false);
  const isStandalone = useIsStandalone();

  // ── Update-Banner ──
  const { updateAvailable, applyUpdate } = useSWUpdate();
  const [showUpdate, setShowUpdate] = useState(false);

  // Install-Banner mit Verzögerung anzeigen
  useEffect(() => {
    if (!canInstall) return;

    const timer = setTimeout(() => {
      setShowInstall(true);
    }, INSTALL_DELAY);

    return () => clearTimeout(timer);
  }, [canInstall]);

  // iOS-Hinweis prüfen
  useEffect(() => {
    if (isStandalone) return;
    if (!isIOS()) return;

    try {
      const dismissedAt = localStorage.getItem(IOS_DISMISS_KEY);
      if (dismissedAt && Date.now() - parseInt(dismissedAt) < IOS_DISMISS_DURATION) return;
    } catch {}

    const timer = setTimeout(() => {
      setShowIOSHint(true);
    }, INSTALL_DELAY);

    return () => clearTimeout(timer);
  }, [isStandalone]);

  // Update-Banner sofort anzeigen
  useEffect(() => {
    if (updateAvailable) {
      setShowUpdate(true);
    }
  }, [updateAvailable]);

  // ── Animations-Klassen ──
  const bannerBase =
    'fixed z-[9999] left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px] rounded-2xl shadow-2xl border border-border-light bg-bg-default p-4 transition-all duration-500';
  const bannerBottom = 'bottom-4';
  const bannerTop = 'top-4';

  return (
    <>
      {/* ── UPDATE-BANNER (höchste Priorität, oben) ── */}
      {showUpdate && (
        <div
          className={`${bannerBase} ${bannerTop} animate-in slide-in-from-top-4`}
          role="alert"
        >
          <div className="flex items-start gap-3">
            {/* Update-Icon */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary">
                {t('updateTitle')}
              </p>
              <p className="mt-0.5 text-xs text-text-muted">
                {t('updateDescription')}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={applyUpdate}
                  className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700 active:bg-blue-800"
                >
                  {t('updateButton')}
                </button>
                <button
                  onClick={() => setShowUpdate(false)}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-text-muted transition-colors hover:bg-bg-subtle"
                >
                  {t('laterButton')}
                </button>
              </div>
            </div>
            {/* Schließen */}
            <button
              onClick={() => setShowUpdate(false)}
              className="shrink-0 rounded-lg p-1 text-text-muted transition-colors hover:bg-bg-subtle hover:text-text-primary"
              aria-label={t('close')}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── INSTALL-BANNER (Chrome/Edge/Android, unten) ── */}
      {showInstall && !showUpdate && (
        <div
          className={`${bannerBase} ${bannerBottom} animate-in slide-in-from-bottom-4`}
          role="alert"
        >
          <div className="flex items-start gap-3">
            {/* App-Icon */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-primary/10 text-brand-primary">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary">
                {t('installTitle')}
              </p>
              <p className="mt-0.5 text-xs text-text-muted">
                {t('installDescription')}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={async () => {
                    const accepted = await promptInstall();
                    if (accepted) setShowInstall(false);
                    else setShowInstall(false);
                  }}
                  className="rounded-lg bg-brand-primary px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-primary/90 active:bg-brand-primary/80"
                >
                  {t('installButton')}
                </button>
                <button
                  onClick={() => {
                    dismiss();
                    setShowInstall(false);
                  }}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-text-muted transition-colors hover:bg-bg-subtle"
                >
                  {t('dismissButton')}
                </button>
              </div>
            </div>
            {/* Schließen */}
            <button
              onClick={() => {
                dismiss();
                setShowInstall(false);
              }}
              className="shrink-0 rounded-lg p-1 text-text-muted transition-colors hover:bg-bg-subtle hover:text-text-primary"
              aria-label={t('close')}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── iOS INSTALL-HINWEIS (Safari, unten) ── */}
      {showIOSHint && !showInstall && !showUpdate && (
        <div
          className={`${bannerBase} ${bannerBottom} animate-in slide-in-from-bottom-4`}
          role="alert"
        >
          <div className="flex items-start gap-3">
            {/* Safari-Icon */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-primary/10 text-brand-primary">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary">
                {t('iosTitle')}
              </p>
              <p className="mt-0.5 text-xs text-text-muted">
                {t('iosDescription')}{' '}
                <span className="inline-flex items-center">
                  <svg className="mx-0.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15m0-3l-3-3m0 0l-3 3m3-3v11.25" />
                  </svg>
                </span>
                {' '}{t('iosDescriptionAfter')} <strong>&quot;{t('iosAddToHome')}&quot;</strong>
              </p>
            </div>
            {/* Schließen */}
            <button
              onClick={() => {
                setShowIOSHint(false);
                try { localStorage.setItem(IOS_DISMISS_KEY, String(Date.now())); } catch {}
              }}
              className="shrink-0 rounded-lg p-1 text-text-muted transition-colors hover:bg-bg-subtle hover:text-text-primary"
              aria-label={t('close')}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
